import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { Link, useLoaderData, useNavigate } from "react-router-dom";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SyncIcon from "@mui/icons-material/Sync";
import ViewIcon from "@mui/icons-material/Visibility";

import { toast } from "react-toastify";
import DefaultApiService from "../../../../../infrastructure/api/default";
import { formatNumber } from "../../../../../infrastructure/utils/formatters";
import { toJalaliDate } from "../../../../middleware/middleware";
import useGetPageInfo from "../../../../common/hooks/useGetPageInfo";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import DeleteConfirm from "../../../../components/shared/Dialog/DeleteConfirm";
import EnhancedTable from "../../../../components/shared/table";

const resolvePackageUpdatedAt = (row) =>
  row?.updateOn ||
  row?.UpdateOn ||
  row?.createOn ||
  row?.CreateOn ||
  null;

const ContractorPackageManagement = () => {
  const pageInfo = useGetPageInfo();
  const dataList = useLoaderData("root");
  const navigate = useNavigate();
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [packageDetails, setPackageDetails] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState(null);
  const [copyingId, setCopyingId] = useState(null);

  const baseUrl = window.globalConfig?.API_URL || "";

  const headCells = [
    {
      id: "title",
      label: "عنوان پکیج",
    },
    {
      id: "updateOn",
      label: "تاریخ بروزرسانی",
      render: (_value, row) => {
        const value = resolvePackageUpdatedAt(row);
        return value ? toJalaliDate(value) : "—";
      },
    },
    {
      id: "plantCapacity",
      label: "ظرفیت",
      render: (_value, row) => {
        const capacityMap = {
          P5KW: "5 کیلووات",
          P10KW: "10 کیلووات",
          P15KW: "15 کیلووات",
          P20KW: "20 کیلووات",
        };
        return capacityMap[row.plantCapacity] || row.plantCapacity || "-";
      },
    },
    {
      id: "phaseCount",
      label: "نوع نیروگاه",
      render: (_value, row) => {
        const phase = row.phaseCount ?? row.PhaseCount;
        if (phase === 1 || phase === "SinglePhase" || phase === "1") return "یک‌فاز";
        if (phase === 3 || phase === "ThreePhase" || phase === "3") return "سه‌فاز";
        return "—";
      },
    },
    {
      id: "priceWithoutEquipment",
      label: "قیمت بدون تجهیزات",
      type: "price",
    },
    {
      id: "priceWithEquipment",
      label: "قیمت با تجهیزات",
      type: "price",
    },
    {
      id: "priceValidUntil",
      label: "اعتبار قیمت",
      render: (_value, row) => {
        const value = row.priceValidUntil || row.PriceValidUntil;
        if (!value) return "—";
        try {
          return toJalaliDate(value);
        } catch {
          return String(value);
        }
      },
    },
    {
      id: "warrantyMonths",
      label: "گارانتی (ماه)",
    },
    {
      id: "status",
      label: "وضعیت",
      render: (_value, row) => {
        const statusMap = {
          1: { label: "پیش‌نویس", color: "default" },
          2: { label: "فعال", color: "success" },
          3: { label: "غیرفعال", color: "error" },
          4: { label: "در حال بررسی", color: "warning" },
          5: { label: "رد شده", color: "error" },
          6: { label: "بایگانی", color: "default" },
        };
        const statusInfo = statusMap[row.status] || {
          label: "نامشخص",
          color: "default",
        };
        return statusInfo.label;
      },
    },
  ];

  const onViewClicked = async (row) => {
    try {
      setSelectedPackage(row);
      console.log("Fetching package details for ID:", row.id);

      const response = await DefaultApiService.getApi(
        `SLN/Contractor/PackageManagement/GetById`,
        { id: row.id },
        true
      );

      console.log("Package details response:", response);

      if (response?.isSuccess && response?.data) {
        const packageData = response.data.package || response.data;
        console.log("Package data:", packageData);
        setPackageDetails(packageData);
        setViewModalOpen(true);
      } else {
        toast.error(response?.message || "خطا در دریافت اطلاعات پکیج");
      }
    } catch (error) {
      console.error("Error fetching package details:", error);
      toast.error("خطا در دریافت اطلاعات پکیج");
    }
  };

  const onEditClicked = (row) => {
    navigate(`${row.id}`);
  };

  const onCopyClicked = async (row) => {
    if (!row?.id || copyingId) return;
    if (!window.confirm(`پکیج «${row.title || row.Title || ""}» کپی شود؟`)) return;
    setCopyingId(row.id);
    try {
      const response = await DefaultApiService.postApi(
        `SLN/Contractor/PackageManagement/Copy?id=${row.id}`,
        {}
      );
      if (response?.isSuccess === false) {
        toast.error(response?.message || "کپی پکیج ناموفق بود");
        return;
      }
      const copied = response?.data ?? response;
      const newId = copied?.packageId || copied?.PackageId || copied?.id || copied?.Id;
      toast.success(response?.message || "پکیج کپی شد");
      if (newId) {
        navigate(`${newId}`);
      } else {
        navigate(".", { replace: true });
      }
    } catch (error) {
      toast.error(error?.message || "کپی پکیج ناموفق بود");
    } finally {
      setCopyingId(null);
    }
  };

  const onDeleteClicked = (row) => {
    setPackageToDelete(row);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!packageToDelete) return { isSuccess: false, message: "خطا" };

    const response = await DefaultApiService.deleteApi(
      `SLN/Contractor/PackageManagement/Delete/${packageToDelete.id}`,
      {},
      true
    );

    if (response?.isSuccess) {
      navigate(".", { replace: true });
    }

    return response;
  };

  const handleCancelDelete = (shouldRefresh) => {
    setDeleteModalOpen(false);
    setPackageToDelete(null);
    if (shouldRefresh) {
      navigate(".", { replace: true });
    }
  };

  const onFetchData = () => {
    navigate(".", { replace: true });
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setSelectedPackage(null);
    setPackageDetails(null);
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      1: { label: "پیش‌نویس", color: "default" },
      2: { label: "فعال", color: "success" },
      3: { label: "غیرفعال", color: "error" },
      4: { label: "در حال بررسی", color: "warning" },
      5: { label: "رد شده", color: "error" },
      6: { label: "بایگانی", color: "default" },
    };
    return statusMap[status] || { label: "نامشخص", color: "default" };
  };

  const getPlantCapacityLabel = (capacity) => {
    const capacityMap = {
      P5KW: "5 کیلووات",
      P10KW: "10 کیلووات",
      P15KW: "15 کیلووات",
      P20KW: "20 کیلووات",
    };
    return capacityMap[capacity] || capacity;
  };

  const getRowStatus = (row) => {
    switch (row.status) {
      case 1:
        return "gray";
      case 2:
        return "success";
      case 3:
        return "danger";
      case 4:
        return "warning";
      case 5:
        return "danger";
      case 6:
        return "gray";
      default:
        return "default";
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12} md={12}>
        <Card>
          <CardHeader
            title={pageInfo.title}
            action={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "fit-content",
                }}
              >
                <Link to={"create"}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                  >
                    ایجاد
                  </Button>
                </Link>

                <Divider
                  orientation="vertical"
                  variant="middle"
                  flexItem
                  sx={{ mx: 1 }}
                />

                <IconButton
                  variant="text"
                  color="primary"
                  onClick={onFetchData}
                >
                  <SyncIcon />
                </IconButton>
              </Box>
            }
          />

          <CardContent>
            <Grid container>
              <Grid item xs={12} md={12}>
                <EnhancedTable
                  rowKey={"id"}
                  rows={dataList}
                  headCells={headCells}
                  pageSize={100}
                  actions={[
                    {
                      icon: <ViewIcon sx={{ width: "1rem", height: "1rem" }} />,
                      tooltip: "مشاهده",
                      onClick: onViewClicked,
                      color: "info",
                    },
                    {
                      icon: <EditIcon sx={{ width: "1rem", height: "1rem" }} />,
                      tooltip: "ویرایش",
                      onClick: onEditClicked,
                      color: "primary",
                    },
                    {
                      icon: (
                        <ContentCopyIcon sx={{ width: "1rem", height: "1rem" }} />
                      ),
                      tooltip: "کپی پکیج",
                      onClick: onCopyClicked,
                      color: "secondary",
                    },
                    {
                      icon: (
                        <DeleteIcon sx={{ width: "1rem", height: "1rem" }} />
                      ),
                      tooltip: "حذف",
                      onClick: onDeleteClicked,
                      color: "error",
                    },
                  ]}
                  getRowStatus={getRowStatus}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* View Package Modal */}
      <Dialog
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">جزئیات پکیج</Typography>
            <IconButton onClick={handleCloseViewModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {packageDetails && (
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary">
                  اطلاعات اصلی
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              {packageDetails.imageUrl && (
                <Grid item xs={12} md={4}>
                  <img
                    src={`${baseUrl}${packageDetails.imageUrl}`}
                    alt={packageDetails.packageTitle || packageDetails.title}
                    style={{
                      width: "100%",
                      maxHeight: "300px",
                      objectFit: "cover",
                      borderRadius: "8px",
                    }}
                  />
                </Grid>
              )}

              <Grid item xs={12} md={packageDetails.imageUrl ? 8 : 12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      عنوان پکیج:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {packageDetails.packageTitle || packageDetails.title}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      ظرفیت:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {getPlantCapacityLabel(packageDetails.plantCapacity)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      نوع نیروگاه:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {(() => {
                        const phase = packageDetails.phaseCount ?? packageDetails.PhaseCount;
                        if (phase === 1 || phase === "SinglePhase" || phase === "1") return "یک‌فاز";
                        if (phase === 3 || phase === "ThreePhase" || phase === "3") return "سه‌فاز";
                        return "—";
                      })()}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      وضعیت:
                    </Typography>
                    <Chip
                      label={getStatusLabel(packageDetails.status).label}
                      color={getStatusLabel(packageDetails.status).color}
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      وضعیت فعال/غیرفعال:
                    </Typography>
                    <Chip
                      label={packageDetails.isActive ? "فعال" : "غیرفعال"}
                      color={packageDetails.isActive ? "success" : "default"}
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      توضیحات:
                    </Typography>
                    <Typography variant="body1">
                      {packageDetails.description || "-"}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>

              {/* Pricing Information */}
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  gutterBottom
                  color="primary"
                  sx={{ mt: 2 }}
                >
                  اطلاعات قیمت‌گذاری
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  قیمت با تجهیزات:
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {packageDetails.priceWithEquipment
                    ? `${formatNumber(packageDetails.priceWithEquipment)} ریال`
                    : "-"}
                </Typography>
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  قیمت بدون تجهیزات:
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {packageDetails.priceWithoutEquipment
                    ? `${formatNumber(
                        packageDetails.priceWithoutEquipment
                      )} ریال`
                    : "-"}
                </Typography>
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  هزینه ارسال:
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {formatNumber(packageDetails.deliveryFee || 0)} ریال
                </Typography>
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  پرداخت ارسال توسط پیمانکار:
                </Typography>
                <Chip
                  label={packageDetails.contractorPaysDelivery ? "بله" : "خیر"}
                  color={
                    packageDetails.contractorPaysDelivery
                      ? "success"
                      : "default"
                  }
                  size="small"
                />
              </Grid>

              {/* Installation & Warranty */}
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  gutterBottom
                  color="primary"
                  sx={{ mt: 2 }}
                >
                  نصب و گارانتی
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  مدت اجرا (روز):
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {packageDetails.installationDurationDays || "-"}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  مدت گارانتی (ماه):
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {packageDetails.warrantyMonths || "-"}
                </Typography>
              </Grid>

              {/* Equipments */}
              {packageDetails.packageEquipments &&
                packageDetails.packageEquipments.length > 0 && (
                  <>
                    <Grid item xs={12}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        color="primary"
                        sx={{ mt: 2 }}
                      >
                        تجهیزات
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>
                    <Grid item xs={12}>
                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>نوع تجهیز</TableCell>
                              <TableCell>برند</TableCell>
                              <TableCell>مدل</TableCell>
                              <TableCell>تعداد</TableCell>
                              <TableCell>قیمت واحد</TableCell>
                              <TableCell>قیمت کل</TableCell>
                              <TableCell>گارانتی</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {packageDetails.packageEquipments.map(
                              (eq, index) => (
                                <TableRow key={index}>
                                  <TableCell>
                                    {eq.equipmentType || "-"}
                                  </TableCell>
                                  <TableCell>{eq.brand || "-"}</TableCell>
                                  <TableCell>{eq.model || "-"}</TableCell>
                                  <TableCell>{eq.quantity || "-"}</TableCell>
                                  <TableCell>
                                    {eq.unitPrice
                                      ? `${formatNumber(eq.unitPrice)} ریال`
                                      : "-"}
                                  </TableCell>
                                  <TableCell>
                                    {eq.totalPrice
                                      ? `${formatNumber(eq.totalPrice)} ریال`
                                      : "-"}
                                  </TableCell>
                                  <TableCell>
                                    {eq.warrantyPeriod || "-"}
                                  </TableCell>
                                </TableRow>
                              )
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  </>
                )}

              {/* Features */}
              {packageDetails.packageFeatures &&
                packageDetails.packageFeatures.length > 0 && (
                  <>
                    <Grid item xs={12}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        color="primary"
                        sx={{ mt: 2 }}
                      >
                        ویژگی‌ها
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>
                    <Grid item xs={12}>
                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>عنوان ویژگی</TableCell>
                              <TableCell>مقدار</TableCell>
                              <TableCell>توضیحات</TableCell>
                              <TableCell>ترتیب نمایش</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {packageDetails.packageFeatures.map((ft, index) => (
                              <TableRow key={index}>
                                <TableCell>{ft.featureTitle || "-"}</TableCell>
                                <TableCell>{ft.featureValue || "-"}</TableCell>
                                <TableCell>{ft.description || "-"}</TableCell>
                                <TableCell>{ft.displayOrder || "-"}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  </>
                )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewModal} color="primary">
            بستن
          </Button>
          <Button
            onClick={() => {
              handleCloseViewModal();
              onEditClicked(selectedPackage);
            }}
            color="primary"
            variant="contained"
          >
            ویرایش
          </Button>
          <Button
            onClick={() => {
              handleCloseViewModal();
              onDeleteClicked(selectedPackage);
            }}
            color="error"
            variant="contained"
          >
            حذف
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirm
        open={deleteModalOpen}
        onClose={handleCancelDelete}
        onSubmit={handleConfirmDelete}
        message={`آیا از حذف پکیج "${packageToDelete?.title}" اطمینان دارید؟`}
      />
    </Grid>
  );
};

export default ContractorPackageManagement;
